/**
 * ChapterList - 章節列表
 * 支援分組展開/收合、編輯模式、批量刪除
 */

import { useState, useMemo } from 'react';
import type { Chapter, Novel } from '../../services/types';
import { ChapterItem } from './ChapterItem';
import { ChapterGroup, CHAPTERS_PER_GROUP } from './types';

export interface ChapterListProps {
  novel: Novel;
  completedChapters: Chapter[];
  userProgressChapterId: string | null;
  isAdmin: boolean;
  isEditMode: boolean;
  selectedChapterIds: Set<string>;
  recrawlingChapterId: string | null;
  skippingChapterId: string | null;
  onEditContent: (chapter: Chapter) => void;
  onRecrawl: (chapter: Chapter) => void;
  onSkip: (chapter: Chapter) => void;
  onMoveChapter: (chapter: Chapter, direction: 'up' | 'down') => void;
  onDeleteChapter: (chapter: Chapter) => void;
  onToggleChapterSelect: (chapterId: string) => void;
  onSelectAll: () => void;
  onBatchDelete: () => void;
  onExitEditMode: () => void;
  onEnterEditMode: () => void;
  onShowAddChapters: () => void;
}

export function ChapterList({
  novel,
  completedChapters,
  userProgressChapterId,
  isAdmin,
  isEditMode,
  selectedChapterIds,
  recrawlingChapterId,
  skippingChapterId,
  onEditContent,
  onRecrawl,
  onSkip,
  onMoveChapter,
  onDeleteChapter,
  onToggleChapterSelect,
  onSelectAll,
  onBatchDelete,
  onExitEditMode,
  onEnterEditMode,
  onShowAddChapters,
}: ChapterListProps) {
  // 章節分組
  const chapterGroups = useMemo(() => {
    const groups: ChapterGroup[] = [];
    for (let i = 0; i < completedChapters.length; i += CHAPTERS_PER_GROUP) {
      const groupChapters = completedChapters.slice(i, i + CHAPTERS_PER_GROUP);
      groups.push({
        start: groupChapters[0]?.chapter_index || 0,
        end: groupChapters[groupChapters.length - 1]?.chapter_index || 0,
        chapters: groupChapters,
      });
    }
    return groups;
  }, [completedChapters]);

  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set([0]));

  const toggleGroup = (index: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleAllGroups = () => {
    if (expandedGroups.size === chapterGroups.length) {
      setExpandedGroups(new Set([0]));
    } else {
      setExpandedGroups(new Set(chapterGroups.map((_, i) => i)));
    }
  };

  return (
    <div className="card overflow-hidden">
      <div className="card-header">
        <div className="flex items-center justify-between">
          {isEditMode ? (
            // 編輯模式 header
            <div className="flex items-center gap-3">
              <button onClick={onSelectAll} className="btn btn-ghost btn-sm">
                {selectedChapterIds.size === completedChapters.length ? '取消全選' : '全選'}
              </button>
              <span className="body-small">已選取 {selectedChapterIds.size} 個章節</span>
            </div>
          ) : (
            <h2 className="heading-section">
              章節列表
              <span className="ml-2 body-small font-normal">
                ({completedChapters.length} 可閱讀)
              </span>
            </h2>
          )}
          <div className="flex items-center gap-2">
            {isEditMode ? (
              // 編輯模式按鈕
              <>
                <button
                  onClick={onBatchDelete}
                  disabled={selectedChapterIds.size === 0}
                  className="btn btn-destructive btn-sm"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  刪除 ({selectedChapterIds.size})
                </button>
                <button onClick={onExitEditMode} className="btn btn-ghost btn-sm">
                  取消
                </button>
              </>
            ) : (
              // 正常模式按鈕
              <>
                {/* 上傳式小說：新增章節按鈕 */}
                {isAdmin && novel.source_type === 'uploaded' && (
                  <button onClick={onShowAddChapters} className="btn btn-primary btn-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    新增章節
                  </button>
                )}
                {/* 上傳式小說：編輯模式按鈕 */}
                {isAdmin && novel.source_type === 'uploaded' && completedChapters.length > 0 && (
                  <button onClick={onEnterEditMode} className="btn btn-outline btn-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    編輯
                  </button>
                )}
                {chapterGroups.length > 1 && (
                  <button onClick={toggleAllGroups} className="btn btn-ghost btn-sm">
                    {expandedGroups.size === chapterGroups.length ? '收合全部' : '展開全部'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {completedChapters.length === 0 ? (
        <div className="py-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <p className="body-base">尚無可閱讀的章節</p>
        </div>
      ) : chapterGroups.length === 1 ? (
        // 章節少於 50，直接顯示
        <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
          {completedChapters.map((chapter, index) => (
            <ChapterItem
              key={chapter.id}
              chapter={chapter}
              novelId={novel.id}
              isLastRead={chapter.id === userProgressChapterId}
              isAdmin={isAdmin}
              isUploadedNovel={novel.source_type === 'uploaded'}
              isEditMode={isEditMode}
              isSelected={selectedChapterIds.has(chapter.id)}
              onEditContent={onEditContent}
              onRecrawl={onRecrawl}
              onSkip={onSkip}
              onMoveUp={(c) => onMoveChapter(c, 'up')}
              onMoveDown={(c) => onMoveChapter(c, 'down')}
              onDelete={onDeleteChapter}
              onToggleSelect={onToggleChapterSelect}
              isRecrawling={recrawlingChapterId === chapter.id}
              isSkipping={skippingChapterId === chapter.id}
              isFirst={index === 0}
              isLast={index === completedChapters.length - 1}
            />
          ))}
        </div>
      ) : (
        // 章節分組顯示
        <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
          {chapterGroups.map((group, index) => (
            <div key={index}>
              <button
                onClick={() => toggleGroup(index)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
              >
                <span className="font-medium text-neutral-700 dark:text-neutral-300">
                  第 {group.start} - {group.end} 章
                </span>
                <div className="flex items-center gap-2">
                  <span className="body-small">{group.chapters.length} 章</span>
                  <svg
                    className={`w-5 h-5 text-neutral-400 transition-transform ${expandedGroups.has(index) ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>
              {expandedGroups.has(index) && (
                <div className="divide-y divide-neutral-50 dark:divide-neutral-700/50 bg-neutral-50/50 dark:bg-neutral-800/50">
                  {group.chapters.map((chapter) => {
                    const globalIndex = completedChapters.findIndex((c) => c.id === chapter.id);
                    return (
                      <ChapterItem
                        key={chapter.id}
                        chapter={chapter}
                        novelId={novel.id}
                        isLastRead={chapter.id === userProgressChapterId}
                        isAdmin={isAdmin}
                        isUploadedNovel={novel.source_type === 'uploaded'}
                        isEditMode={isEditMode}
                        isSelected={selectedChapterIds.has(chapter.id)}
                        onEditContent={onEditContent}
                        onRecrawl={onRecrawl}
                        onSkip={onSkip}
                        onMoveUp={(c) => onMoveChapter(c, 'up')}
                        onMoveDown={(c) => onMoveChapter(c, 'down')}
                        onDelete={onDeleteChapter}
                        onToggleSelect={onToggleChapterSelect}
                        isRecrawling={recrawlingChapterId === chapter.id}
                        isSkipping={skippingChapterId === chapter.id}
                        isFirst={globalIndex === 0}
                        isLast={globalIndex === completedChapters.length - 1}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
